import React, { useState, useEffect } from 'react';
import '../styles/learn.css';
import ContentRenderer from '../components/ContentRenderer';
import {
    getChapterProjectStatus,
    markChapterProjectViewed,
    markChapterProjectComplete
} from '../context/engine.ts';

function ChapterProject({
    chapter,
    currentUser,
    currentCourse,
    onBack
}) {
    const [projectStatus, setProjectStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isMarkingComplete, setIsMarkingComplete] = useState(false);

    // Fetch project status and mark as viewed
    useEffect(() => {
        const fetchProjectStatus = async () => {
            if (!chapter?.id || !currentUser?.id) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // Get project status
                const status = await getChapterProjectStatus(currentUser.id, chapter.id);
                setProjectStatus(status);

                // Mark as viewed if it's unlocked and not yet viewed
                if (status.unlocked && !status.viewed) {
                    await markChapterProjectViewed(currentUser.id, chapter.id);
                    setProjectStatus({ ...status, viewed: true });
                }
            } catch (err) {
                setError('Failed to load project. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchProjectStatus();
    }, [chapter?.id, currentUser?.id]);

    const handleToggleComplete = async () => {
        if (!chapter?.id || !currentUser?.id) return;

        try {
            setIsMarkingComplete(true);
            const newStatus = !projectStatus.markedComplete;

            await markChapterProjectComplete(currentUser.id, chapter.id, newStatus);
            setProjectStatus({ ...projectStatus, markedComplete: newStatus });
        } catch (err) {
            alert('Failed to update project status. Please try again.');
        } finally {
            setIsMarkingComplete(false);
        }
    };

    if (loading) {
        return (
            <div className="chapter-project-container">
                <div className="project-loading">
                    <div className="spinner"></div>
                    <p>Loading chapter project...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="chapter-project-container">
                <div className="project-error">
                    <h3>Error Loading Project</h3>
                    <p>{error}</p>
                    <button onClick={onBack} className="btn-back">
                        Back to Sections
                    </button>
                </div>
            </div>
        );
    }

    if (!projectStatus?.unlocked) {
        return (
            <div className="chapter-project-container">
                <div className="project-locked">
                    <div className="lock-icon">🔒</div>
                    <h3>Chapter Project Locked</h3>
                    <p>Complete all section quizzes in this chapter with a passing score (60% or higher) to unlock the chapter project.</p>
                    <button onClick={onBack} className="btn-back">
                        Back to Sections
                    </button>
                </div>
            </div>
        );
    }

    if (!projectStatus?.instructions) {
        return (
            <div className="chapter-project-container">
                <div className="project-generating">
                    <div className="spinner"></div>
                    <h3>Generating Your Project...</h3>
                    <p>We're creating a hands-on project for this chapter. This should only take a moment.</p>
                    <button onClick={onBack} className="btn-back">
                        Back to Sections
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="chapter-project-container">
            <div className="project-header">
                <button onClick={onBack} className="btn-back">
                    ← Back to Sections
                </button>
                <div className="project-title">
                    <h2>Chapter Project: {chapter.name}</h2>
                    {projectStatus.markedComplete && (
                        <span className="project-badge completed">✓ Completed</span>
                    )}
                </div>
            </div>

            <div className="project-content">
                <div className="project-intro">
                    <p className="intro-text">
                        🎯 This hands-on project helps you apply what you've learned in this chapter.
                        Work through it at your own pace and mark it complete when you're done.
                    </p>
                </div>

                <div className="project-instructions">
                    <ContentRenderer content={projectStatus.instructions} />
                </div>

                <div className="project-actions">
                    <button
                        onClick={handleToggleComplete}
                        disabled={isMarkingComplete}
                        className={`btn-toggle-complete ${projectStatus.markedComplete ? 'completed' : ''}`}
                    >
                        {isMarkingComplete ? (
                            'Updating...'
                        ) : projectStatus.markedComplete ? (
                            '✓ Mark as Incomplete'
                        ) : (
                            'Mark as Complete'
                        )}
                    </button>

                    <button onClick={onBack} className="btn-continue">
                        Continue to Next Chapter →
                    </button>
                </div>

                <div className="project-note">
                    <p>
                        <strong>Note:</strong> This project is optional. You can return to it anytime
                        and move forward to the next chapter whenever you're ready.
                    </p>
                </div>
            </div>
        </div>
    );
}

export default ChapterProject;
